import { Maatregel, OmleidingMaatregel, StopMaatregel, VertrekTijd } from "@/entities/vsm";
import { StationMultiSelect } from "@/features/infra/components/stations-multiselect.tsx";
import { Form, FormFieldInput } from "@/shared/components";
import { uuid } from "@/shared/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { FormButtons, MaatregelTypeSwitch, OpmerkingField, SerieInput, StationSelect } from "./components.tsx";

const aanvullendSchema = z.discriminatedUnion("type", [
    z.object({
        type: z.enum(["ExtraStop", "StopVerwijderen"]),
        treinserie: z.string().min(1, "Serie is verplicht"),
        opStationCode: z
            .string({ error: (issue) => (issue.input === undefined ? "Verplicht" : undefined) })
            .min(1, "Van station is verplicht"),
        richtingStationCode: z.string().optional(),
        opmerking: z.string().optional(),
        vertrekTijd: VertrekTijd.zodType,
    }),

    z.object({
        type: z.literal("Omleiding"),
        treinserie: z.string().min(1, "Serie is verplicht"),
        vanStationCode: z
            .string({ error: (issue) => (issue.input === undefined ? "Verplicht" : undefined) })
            .min(1, "Van station is verplicht"),
        naarStationCode: z
            .string({ error: (issue) => (issue.input === undefined ? "Verplicht" : undefined) })
            .min(1, "Naar station is verplicht"),
        opmerking: z.string().optional(),
        viaStationCodes: z.array(z.string()).min(1, "Minimaal één via-station is verplicht"),
    }),
]);

export type AanvullendeMaatregelData = z.infer<typeof aanvullendSchema>;

interface AanvullendeMaatregelFormProps {
    type: "create" | "update";
    onSubmit: (data: Maatregel) => void;
    onCancel: () => void;
    initialData?: OmleidingMaatregel | StopMaatregel | undefined;
}

export function AanvullendeMaatregelForm({ onSubmit, onCancel, type: formType, initialData }: Readonly<AanvullendeMaatregelFormProps>) {
    const form = useForm<AanvullendeMaatregelData>({
        resolver: zodResolver(aanvullendSchema),
        defaultValues: {
            type: initialData?.type ?? "Omleiding",
            treinserie: initialData?.treinserie ?? "",
            vanStationCode: initialData?.vanStationCode ?? "",
            naarStationCode: initialData?.naarStationCode ?? "",
            opStationCode: initialData?.vanStationCode ?? "",
            richtingStationCode: initialData?.naarStationCode ?? "",
            viaStationCodes: initialData?.type === "Omleiding" ? initialData?.omleiding.viaStationCodes : [],
            opmerking: initialData?.opmerking ?? "",
            vertrekTijd: VertrekTijd.toMinutes(initialData?.vertrekTijd),
        },
    });

    const isDirty = form.formState.isDirty;
    const type = form.watch("type");
    const treinserie = form.watch("treinserie");
    const opStationPlaceholder = type === "ExtraStop" ? "Stopt op" : "Stopt niet op";

    const onSubmitForm = (data: AanvullendeMaatregelData) => {
        if (!isDirty) {
            onCancel();
            return;
        }
        let maatregel = convertToMaatregel(data);
        if (formType === "update" && initialData) {
            maatregel = Maatregel.preserveMetadataForUpdate(maatregel, initialData.id, initialData.handmatigToegevoegd);
        }
        onSubmit(maatregel);
    };

    return (
        <div className="bg-emphasis-primary relative rounded-lg p-4">
            <Form {...form}>
                <form onSubmit={(e) => void form.handleSubmit(onSubmitForm)(e)} className="space-y-2">
                    <div className="flex flex-row gap-4">
                        <SerieInput form={form} name="treinserie" />
                        <MaatregelTypeSwitch form={form} name="type" />
                    </div>

                    {type === "Omleiding" ? (
                        <>
                            <div className="flex items-center gap-4">
                                <StationSelect form={form} name="vanStationCode" placeholder="Van" forTreinserie={treinserie} />
                                <StationSelect form={form} name="naarStationCode" placeholder="Naar" forTreinserie={treinserie} />
                            </div>
                            <StationMultiSelect
                                form={form}
                                name="viaStationCodes"
                                label={null}
                                placeholder="Via (meerdere)"
                                badgeLabel="value"
                                data-testid="via-station-codes"
                            />
                        </>
                    ) : (
                        <div className="flex items-center gap-4">
                            <FormFieldInput form={form} name="vertrekTijd" placeholder=":min" className="max-w-[80px]" />
                            <StationSelect form={form} name="opStationCode" placeholder={opStationPlaceholder} forTreinserie={treinserie} />
                            <StationSelect
                                form={form}
                                name="richtingStationCode"
                                placeholder="Richting (optioneel)"
                                forTreinserie={treinserie}
                            />
                        </div>
                    )}

                    <OpmerkingField form={form} name="opmerking" />
                    <FormButtons
                        onCancel={onCancel}
                        isSubmitting={form.formState.isSubmitting}
                        submitLabel={formType === "create" ? "Toevoegen" : "Opslaan"}
                    />
                </form>
            </Form>
        </div>
    );
}

function convertToMaatregel(data: AanvullendeMaatregelData): Maatregel {
    switch (data.type) {
        case "ExtraStop":
        case "StopVerwijderen":
            return {
                id: uuid(),
                type: data.type,
                treinserie: data.treinserie,
                richting: data.richtingStationCode ? "Heen" : "ViceVersa",
                vanStationCode: data.opStationCode,
                naarStationCode: data.richtingStationCode,
                opmerking: data.opmerking,
                vertrekTijd: data.vertrekTijd ? VertrekTijd.toVertrekTijd(data.vertrekTijd) : undefined,
                handmatigToegevoegd: true,
                status: "Nieuw",
                opmerkingIsSpoorwebSuggestie: false,
            } satisfies StopMaatregel;
        case "Omleiding":
            return {
                id: uuid(),
                type: "Omleiding",
                treinserie: data.treinserie,
                richting: "Heen",
                vanStationCode: data.vanStationCode,
                naarStationCode: data.naarStationCode,
                omleiding: {
                    viaStationCodes: data.viaStationCodes,
                },
                opmerking: data.opmerking,
                handmatigToegevoegd: true,
                status: "Nieuw",
                opmerkingIsSpoorwebSuggestie: false,
            } satisfies OmleidingMaatregel;
    }
}
